import { VCard, VCardImage, VCardProperties } from './vcard.generator.model';

import { BusinessCardInformation } from '../interfaces/business-card.interface';
import {
  CompanyAddress,
  CompanyDetails,
  ContactDetails,
  Messages,
  PersonalDetails,
  SocialMedia,
} from '../interfaces/commons.interface';

export class Builder {
  private readonly properties: VCardProperties;

  private constructor() {
    this.properties = [];
  }

  static getInstance(): Builder {
    return new Builder().withStartMarker();
  }

  private withStartMarker(): Builder {
    this.properties.push('BEGIN:VCARD');
    return this;
  }

  private withEndMarker(): Builder {
    this.properties.push('END:VCARD');
    return this;
  }

  private join(): string {
    return this.properties.join('\n');
  }

  withVersion(version: '2.1' | '3.0' | '4.0' = '3.0'): Builder {
    this.properties.push(`VERSION:${version}`);
    return this;
  }

  withFacebook(model: SocialMedia): Builder {
    if (model.facebook) {
      this.properties.push(`URL;type="Facebook";CHARSET=UTF-8:${model.facebook}`);
    }
    return this;
  }

  withLinkedIn(model: SocialMedia): Builder {
    if (model.linkedin) {
      this.properties.push(`URL;type="LinkedIn";CHARSET=UTF-8:${model.linkedin}`);
    }
    return this;
  }

  withXing(model: SocialMedia): Builder {
    if (model.xing) {
      this.properties.push(`URL;type="Xing";CHARSET=UTF-8:${model.xing}`);
    }
    return this;
  }

  withEmail(model: ContactDetails): Builder {
    if (model.email) {
      this.properties.push(`EMAIL;1;CHARSET=UTF-8:${model.email}\n`);
    }
    return this;
  }

  withAdditionalEmail(model: ContactDetails): Builder {
    if (model.additional_email) {
      this.properties.push(`EMAIL;2;CHARSET=UTF-8:${model.additional_email}\n`);
    }
    return this;
  }

  withMobileNumber(model: ContactDetails): Builder {
    if (model.mobile_number) {
      this.properties.push(`TEL;TYPE=CELL:${model.mobile_number}`);
    }
    return this;
  }

  withPhoneNumber(model: ContactDetails): Builder {
    if (model.phone_number) {
      this.properties.push(`TEL;TYPE=WORK,VOICE:${model.phone_number}`);
    }
    return this;
  }

  withFormattedName(model: PersonalDetails): Builder {
    let formattedNameProperty = 'FN;CHARSET=UTF-8:';
    formattedNameProperty += model.title ? `${model.title} ` : '';
    formattedNameProperty += `${model.first_name} ${model.last_name}`;
    this.properties.push(this.replaceUmlauts(formattedNameProperty));
    return this;
  }

  withName(model: PersonalDetails): Builder {
    let nameProperty = 'N;CHARSET=UTF-8:';
    nameProperty += `${model.last_name};`;
    nameProperty += `${model.first_name};;`;
    nameProperty += model.title ? `${model.title};` : ';';
    this.properties.push(this.replaceUmlauts(nameProperty));
    return this;
  }

  withCompanyAddress(model: CompanyAddress): Builder {
    let addressProperty = 'ADR;CHARSET=UTF-8;TYPE=WORK:;;';
    addressProperty += `${model.street_address};`;
    addressProperty += `${model.locality};`;
    addressProperty += (model.region ?? '') + ';';
    addressProperty += `${model.postal_code};`;
    addressProperty += `${model.country_name};`;
    this.properties.push(this.replaceUmlauts(addressProperty));
    return this;
  }

  withCompanyName(model: CompanyDetails): Builder {
    if (model.company_name) {
      this.properties.push(`ORG;CHARSET=UTF-8:${this.replaceUmlauts(model.company_name)}`);
    }
    return this;
  }

  withWebsite(model: CompanyDetails): Builder {
    if (model.website) {
      this.properties.push(`URL;type=WORK;CHARSET=UTF-8:${model.website}`);
    }
    return this;
  }

  withPosition(model: CompanyDetails): Builder {
    if (model.position) {
      this.properties.push(`TITLE;CHARSET=UTF-8:${this.replaceUmlauts(model.position)}`);
    }
    return this;
  }

  withMessages(model: Messages): Builder {
    if (model.business_card_message) {
      this.properties.push(`NOTE;CHARSET=UTF-8:${this.replaceUmlauts(model.business_card_message)}`);
    }
    return this;
  }

  withRevision(model: BusinessCardInformation): Builder {
    this.properties.push(`REV:${model.modified_at}`);
    return this;
  }

  withPhoto(image: VCardImage | undefined): Builder {
    if (image) {
      this.properties.push(`PHOTO;ENCODING=b;TYPE=${image.type}:${image.dataUrl}`);
    }
    return this;
  }

  build(): VCard {
    return this.withEndMarker().join();
  }

  replaceUmlauts(text: string): string {
    const umlauts = [
      { source: 'ä', replacement: 'ae' },
      { source: 'ö', replacement: 'oe' },
      { source: 'ü', replacement: 'ue' },
      { source: 'ß', replacement: 'ss' },
      { source: 'Ä', replacement: 'Ae' },
      { source: 'Ö', replacement: 'Oe' },
      { source: 'Ü', replacement: 'Ue' },
    ];

    let modifiedText = text;

    umlauts.forEach((umlaut) => {
      const regex = new RegExp(umlaut.source, 'g');
      modifiedText = modifiedText.replace(regex, umlaut.replacement);
    });

    return modifiedText;
  }
}
